import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;
const RECONNECT_MAX_RETRIES = 30;
const RECONNECT_HEALTH_CHECK_INTERVAL = 30000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.reconnectAttempts = 0;
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          if (BaseActionCableConnector.isDisconnected) {
            BaseActionCableConnector.isDisconnected = false;
            this.reconnectAttempts = 0;
            this.clearReconnectTimer();
            this.onReconnect();
          }
        },
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
    this.startHealthCheck();
  }

  startHealthCheck() {
    this.healthCheckTimer = setInterval(() => {
      if (!this.consumer.connection.isOpen() && !this.reconnectTimer) {
        BaseActionCableConnector.isDisconnected = true;
        this.reconnectAttempts = 0;
        this.initReconnectTimer();
      }
    }, RECONNECT_HEALTH_CHECK_INTERVAL);
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      this.reconnectAttempts = 0;
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.reconnectAttempts += 1;
      if (this.reconnectAttempts > RECONNECT_MAX_RETRIES) {
        // Reset and force a fresh connection attempt instead of giving up
        this.consumer.disconnect();
        this.reconnectAttempts = 0;
        setTimeout(() => {
          this.consumer.connect();
          this.initReconnectTimer();
        }, 10000);
      } else {
        this.initReconnectTimer();
      }
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    const delay = Math.min(
      RECONNECT_INTERVAL * 1.5 ** this.reconnectAttempts,
      10000
    );
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, delay);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.consumer.disconnect();
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
